import { XIcon } from '@phosphor-icons/react'
import styles from './ScoopChip.module.scss'

/**
 * ScoopChip - A compact element for displaying tags, statuses, or small pieces of information
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.label] - Text label to display in the chip
 * @param {React.ReactNode} [props.children] - Custom content to display (overrides label if provided)
 * @param {'small' | 'medium'} [props.size='small'] - Chip size
 *   - small: Compact size (matches MUI Chip size="small")
 *   - medium: Default size
 * @param {'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'critical' | 'watch'} [props.color='default'] - Chip color variant
 * @param {string} [props.className] - Additional CSS classes to apply to the chip
 * @param {Object} [props.style] - Inline styles (highest priority - overrides all other styles)
 * @param {function} [props.onClick] - Optional click handler (makes chip clickable)
 * @param {function} [props.onDelete] - Optional delete handler (shows X icon when provided)
 * @param {...any} props - All other props are passed to the native HTML div element
 *
 */
export const ScoopChip = ({
    label,
    children,
    size = 'small',
    color = 'default',
    className = '',
    onClick,
    onDelete,
    style,
    ...props
}) => {
    const chipClassName = [
        styles.chip,
        styles[size] || styles.small,
        styles[color] || styles.default,
        onClick && styles.clickable,
        className,
    ]
        .filter(Boolean)
        .join(' ')

    const chipContent = children || label

    const handleDelete = (e) => {
        e.stopPropagation()
        onDelete?.(e)
    }

    return (
        <div className={chipClassName} style={style} onClick={onClick} {...props}>
            <span className={styles.label}>{chipContent}</span>
            {onDelete && (
                <XIcon
                    size={size === 'small' ? 14 : 16}
                    className={styles.deleteIcon}
                    onClick={handleDelete}
                />
            )}
        </div>
    )
}

export default ScoopChip
